import { useState, useEffect, useCallback } from 'react';
import {
  getMeConsentsV1,
  updateMeConsentsV1,
} from '../../api/me-—-consents/me-—-consents';
import { ConsentDto, UpdateConsentsRequest } from '../../api/model';

type ConsentsResponse = { status: number; data: ConsentDto[] };

export function useConsents() {
  const [data, setData] = useState<ConsentsResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getMeConsentsV1();
      setData(response as ConsentsResponse);
    } catch {
      // silent — caller checks data === undefined
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, refetch };
}

export function useUpdateConsents() {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = useCallback(async (vars: { data: UpdateConsentsRequest }) => {
    setIsPending(true);
    try {
      return await updateMeConsentsV1(vars.data);
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync, isPending };
}

/**
 * Fetches all consent preferences for the authenticated user.
 *
 * Each record maps a CMS consent type node key to the user's opted-in/out state.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @returns Array of `ConsentDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function getProfileConsents(): Promise<ConsentDto[] | undefined> {
  try {
    const response = await getMeConsentsV1();

    if (response.status === 200) {
      return response.data as ConsentDto[];
    }

    console.warn(`getProfileConsents: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getProfileConsents: request failed:', error);
    return undefined;
  }
}

/**
 * Updates the opted-in/out state for one or more consent types in a single batch.
 *
 * Sends a `PATCH /api/v1/me/consents` with the provided consents. Each entry maps
 * a CMS consent type node key to its new opted-in/out state. Existing consents not
 * included in the request are left unchanged.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param request - Batch of consent updates to apply.
 * @returns Array of updated `ConsentDto` on HTTP 200.
 * @returns `undefined` on 400 (validation error), 401 (unauthenticated), or network error.
 */
export async function updateProfileConsents(
  request: UpdateConsentsRequest
): Promise<ConsentDto[] | undefined> {
  try {
    const response = await updateMeConsentsV1(request);

    if (response.status === 200) {
      return response.data as ConsentDto[];
    }

    console.warn(`updateProfileConsents: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('updateProfileConsents: request failed:', error);
    return undefined;
  }
}
